import React, { useEffect, useState, useContext, useRef } from "react";
import { AppContext } from "../../store/AppContext";
import { DataTable } from "primereact/datatable";
import SelectInput from "../../components/SelectInput";
import Label from "../../components/Label";
import _ from "lodash";
import * as util from "../../shared/Utilities";
import {
  convertCustomerEntityToGridModel,
  getAllConnectorLabels,
  getFailedSyncCustomers,
  getSuccessSyncCustomers,
} from "./services/CustomerService";
import { ValueLabel } from "../../shared/models";
import { CustomerEntityModel, CustomerGridModel } from "./models";
import { TabPanel, TabView } from "primereact/tabview";
import CustomerGridComponent from "./components/CustomerGridComponent";
import CustomerGridErrorComponent from "./components/CustomerGridErrorComponent";
import myobToNeto from "../../assets/myobToNeto.png"
import netoToMyob2Way from "../../assets/netoToMyob2Way.png"
import { getUserConnectorConfiguration } from "../Mappings/NetoToMyob/services/MappingService";

// Customers are served exclusively via the backend REST API (PostgreSQL-backed,
// via CustomerRepository) - no Customer Firestore reads/listeners in React.
const CUSTOMERS_POLL_INTERVAL_MS = 60000;

export const useDebouncedEffect = (effect: any, deps: any, delay: number) => {
  useEffect(() => {
    const handler = setTimeout(() => effect(), delay);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps || [], delay]);
}

const CustomerGridContainer: React.FC = () => {
  let { State, Dispatch } = useContext(AppContext);
  const dt = useRef<DataTable>(null);

  const [connectors, setConnectors] = useState<Array<ValueLabel>>([{ label: "", value: "" },]);
  const [customersData, setcustomersData] = useState<Array<CustomerGridModel>>([]);
  const [customerFailedData, setCustomerFailedData] = useState<Array<CustomerGridModel>>([]);
  const [selectedConnector, setSelectedConnector] = useState<string>(State.selectedConnectorOnGrid ?? "");
  const [loading, setLoading] = useState<boolean>(false);
  const [fLoading, setFLoading] = useState<boolean>(false);
  const [startAfterRecord, setStartAfterRecord] = useState<any>();
  const [startBeforeRecord, setStartBeforeRecord] = useState<any>();
  const [isNextDisabled, setIsNextDisabled] = useState<boolean>(false);
  const [isPrevDisabled, setIsPrevDisabled] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [customersError, setCustomersError] = useState<string>('');
  const [failedCustomersError, setFailedCustomersError] = useState<string>('');
  const [customersLastRefreshedAt, setCustomersLastRefreshedAt] = useState<Date>();
  const [failedCustomersLastRefreshedAt, setFailedCustomersLastRefreshedAt] = useState<Date>();
  // Authoritative connector totals from the API (`total`), not the loaded/page array
  // length - backs the tab/paginator counts so they represent the true connector
  // Synced/Failed Customer totals rather than whatever page happens to be loaded.
  const [successTotal, setSuccessTotal] = useState<number>(0);
  const [failedTotal, setFailedTotal] = useState<number>(0);

  // Prevent overlapping requests (manual refresh / poll / connector-change racing each other).
  const isFetchingSuccessRef = useRef<boolean>(false);
  const isFetchingFailedRef = useRef<boolean>(false);
  const successPollTimerRef = useRef<ReturnType<typeof setInterval>>();
  const failedPollTimerRef = useRef<ReturnType<typeof setInterval>>();

  const filterFields = {
    Username: 'neto.Username',
    DispalyID: 'myob.DisplayID',
    ActiveText: 'neto.Active',
    IsActiveText: 'myob.IsActive',
    CompanyOrUser1: 'myob.CompanyName',
    CompanyOrUser2: 'myob.FirstName',
    CompanyOrUser3: 'myob.LastName',
    syncErrorMessage: 'neto.syncErrorMessage',
    myobSyncErrorMessage: 'myob.syncErrorMessage',
    lastSyncAt: 'lastSyncAt',
  }
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 5,
    page: 1,
    sortField: "lastSyncAt",
    sortOrder: -1,
    filters: undefined,
    pageMode: '',
    pageSize: 50,
  });
  const [customerType, setCustomerType] = useState<string>(filterFields.CompanyOrUser1);
  const [netoToMyobSync, setNetoToMyobSync] = useState(false);
  const [myobToNetoSync, setMyobToNetoSync] = useState(false);


  useEffect(() => {
    (async () => {
      setLoading(true);
      const connectorsList = await getAllConnectorLabels(State.currentUser.currentUserId);
      setLoading(false);
      if (State.currentUser.currentUserId) {
        setSelectedConnector(connectorsList.length && connectorsList[0].value ? connectorsList[0].value : "");
      }
      setConnectors(connectorsList);
    })();

    return () => {
      Dispatch({
        Type: "SET_SALES_ORDER",
        Payload: { connector: "", data: [] },
      });
    };
  }, []);


  useEffect(() => {
    setLoading(true);
    setLazyParams({
      first: 0,
      rows: 5,
      page: 1,
      sortField: "lastSyncAt",
      sortOrder: -1,
      filters: undefined,
      pageMode: '',
      pageSize: 50,
    });
    setcustomersData([]);
    setCustomerFailedData([]);
  }, [selectedConnector]);


  useEffect(() => {
    (async () => {
      if (selectedConnector == "") return
      let userMappings = await getUserConnectorConfiguration(State.currentUser.currentUserId, selectedConnector);
      setMyobToNetoSync(userMappings?.result?.mappings?.myobCustomerToNeto?.isChecked)
    })();

  }, [selectedConnector]);

  useEffect(() => {
    triggerPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  useDebouncedEffect(() => {
    triggerPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnector], 1000);

  useDebouncedEffect(() => { setLoading(true); processPagination(); }, [lazyParams], 1000);

  const buildFiltersArray = () => {
    let filtersArray: any = [];
    if (lazyParams?.filters) {
      const filters: any = lazyParams.filters;
      if (filters['NetoCustomer.Username']) {
        if (!_.isEmpty(filters['NetoCustomer.Username']?.value))
          filtersArray.push({ key: filterFields.Username, value: filters['NetoCustomer.Username']?.value })
      }

      if (filters['MyobCustomer.DisplayID']) {
        if (!_.isEmpty(filters['MyobCustomer.DisplayID']?.value))
          filtersArray.push({ key: filterFields.DispalyID, value: filters['MyobCustomer.DisplayID']?.value })
      }

      if (filters['NetoCustomer.ActiveText']) {
        if (!_.isEmpty(filters['NetoCustomer.ActiveText']?.value))
          filtersArray.push({
            key: filterFields.ActiveText, value: (filters['NetoCustomer.ActiveText']?.value
              === 'Active' ? 'True' : 'False')
          })
      }

      if (filters['MyobCustomer.IsActiveText']) {
        if (!_.isEmpty(filters['MyobCustomer.IsActiveText']?.value))
          filtersArray.push({
            key: filterFields.IsActiveText, value: (filters['MyobCustomer.IsActiveText']?.value
              === 'Active' ? true : false)
          })
      }

      if (filters['CompanyOrUser']) {
        if (!_.isEmpty(filters['CompanyOrUser']?.value))
          filtersArray.push({ key: customerType, value: filters['CompanyOrUser']?.value });
      }

      if (filters['NetoCustomer.syncErrorMessage']) {
        if (!_.isEmpty(filters['NetoCustomer.syncErrorMessage']?.value))
          filtersArray.push({ key: filterFields.syncErrorMessage, value: filters['NetoCustomer.syncErrorMessage']?.value })
      }

      if (filters['MyobCustomer.syncErrorMessage)']) {
        if (!_.isEmpty(filters['MyobCustomer.syncErrorMessage']?.value))
          filtersArray.push({ key: filterFields.myobSyncErrorMessage, value: filters['MyobCustomer.syncErrorMessage']?.value })
      }

      if (filters['lastSyncAt']) {
        if ((filters?.lastSyncAt?.value))
          filtersArray.push({ key: filterFields.lastSyncAt, value: util.getUserTimezoneStartAndEndOfADay(filters?.lastSyncAt?.value) });
      }
    }
    return filtersArray;
  }

  const processPagination = (isBackgroundRefresh: boolean = false) => {
    if (State?.currentUser?.currentUserId && selectedConnector) {
      // Prevent overlapping requests (manual refresh / poll / lazyParams change racing each other)
      if (isFetchingSuccessRef.current) {
        return;
      }
      isFetchingSuccessRef.current = true;
      if (!isBackgroundRefresh) {
        setLoading(true);
      }

      const filtersArray = buildFiltersArray();

      getSuccessSyncCustomers(State.currentUser.currentUserId, selectedConnector,
        lazyParams.pageSize, lazyParams?.sortField, lazyParams?.sortOrder,
        startBeforeRecord, startAfterRecord, lazyParams.pageMode, filtersArray).subscribe({
          next: (e: any) => {
            let customers: any = [];
            e?.customers && _.size(e?.customers) > 0 && e?.customers.map((doc: any, index: number) => {
              let userData: CustomerEntityModel = doc;
              customers.push(convertCustomerEntityToGridModel(userData, index));
            });

            setSuccessTotal(typeof e?.total === 'number' ? e.total : customers.length);
            setCustomersError('');
            setCustomersLastRefreshedAt(new Date());

            if (e?.customers && e?.customers.length > 0) {
              setcustomersData(customers);

              if (e?.firstDocId) {
                setStartBeforeRecord(e?.firstDocId);
              }
              if (e?.lastDocId) {
                setStartAfterRecord(e?.lastDocId);
              }

              if (lazyParams.pageMode === 'ALL') {
                setIsNextDisabled(true);
                setIsPrevDisabled(true);
              } else {
                setIsNextDisabled(false);
                setIsPrevDisabled(false);
              }

              setLoading(false);
            }
            else {
              if (lazyParams.pageMode === 'ALL') {
                setcustomersData([]);
                setIsNextDisabled(true);
                setIsPrevDisabled(true);
              } else if (lazyParams.pageMode === 'PREV') {
                setIsPrevDisabled(true);
              } else if (lazyParams.pageMode === 'NEXT') {
                setIsNextDisabled(true);
              } else {
                setcustomersData([]);
                setIsNextDisabled(true);
                setIsPrevDisabled(true);
              }

              setLoading(false);
            }
            isFetchingSuccessRef.current = false;
          },
          error: (err: any) => {
            console.error('Error fetching customers:', err);
            // Transient polling errors shouldn't spam the user; only surface an
            // error state for the manual/initial fetch, not background polls.
            if (!isBackgroundRefresh) {
              setCustomersError('Unable to load customers. Please try again.');
            }
            isFetchingSuccessRef.current = false;
            setLoading(false);
          }
        });
      util.getTimezoneFromFirebase(State.currentUser.currentUserId, selectedConnector);
    };
  }

  const fetchFailedCustomers = (isBackgroundRefresh: boolean = false) => {
    if (_.isEmpty(selectedConnector) || !State?.currentUser?.currentUserId) {
      setFLoading(false);
      return;
    }
    // Prevent overlapping requests (manual refresh / poll / tab-load racing each other)
    if (isFetchingFailedRef.current) {
      return;
    }
    isFetchingFailedRef.current = true;
    if (!isBackgroundRefresh) {
      setFLoading(true);
    }

    getFailedSyncCustomers(
      State.currentUser.currentUserId, selectedConnector,
      1000, lazyParams?.sortField, lazyParams?.sortOrder,
      undefined, undefined, 'ALL'
    ).subscribe({
      next: (e: any) => {
        let customers: any = [];
        e?.customers && _.size(e?.customers) > 0 && e?.customers.map((doc: any, index: number) => {
          const userData: CustomerEntityModel = doc;
          customers.push(convertCustomerEntityToGridModel(userData, index));
        });

        setCustomerFailedData(customers);
        setFailedTotal(typeof e?.total === 'number' ? e.total : customers.length);
        setFailedCustomersError('');
        setFailedCustomersLastRefreshedAt(new Date());
        isFetchingFailedRef.current = false;
        setFLoading(false);
      },
      error: (err: any) => {
        console.error('Error fetching failed customers:', err);
        if (!isBackgroundRefresh) {
          setFailedCustomersError('Unable to load failed customers. Please try again.');
        }
        isFetchingFailedRef.current = false;
        setFLoading(false);
      }
    });
  };

  // Poll Synced Customers every 60s while that tab is active and the component is
  // mounted. Manual refresh and post-action refreshes reuse processPagination
  // directly, so this is purely the background timer.
  useEffect(() => {
    if (successPollTimerRef.current) {
      clearInterval(successPollTimerRef.current);
      successPollTimerRef.current = undefined;
    }

    if (activeTab === 1 && selectedConnector) {
      successPollTimerRef.current = setInterval(() => {
        processPagination(true);
      }, CUSTOMERS_POLL_INTERVAL_MS);
    }

    return () => {
      if (successPollTimerRef.current) {
        clearInterval(successPollTimerRef.current);
        successPollTimerRef.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedConnector]);

  // Poll Failed Customers every 60s while that tab is active and the component is
  // mounted.
  useEffect(() => {
    if (failedPollTimerRef.current) {
      clearInterval(failedPollTimerRef.current);
      failedPollTimerRef.current = undefined;
    }

    if (activeTab === 0 && selectedConnector) {
      failedPollTimerRef.current = setInterval(() => {
        fetchFailedCustomers(true);
      }, CUSTOMERS_POLL_INTERVAL_MS);
    }

    return () => {
      if (failedPollTimerRef.current) {
        clearInterval(failedPollTimerRef.current);
        failedPollTimerRef.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedConnector]);

  const refreshCustomers = () => {
    processPagination(false);
  }

  const refreshFailedCustomers = () => {
    fetchFailedCustomers(false);
  }

  const triggerPagination = () => {
    if (activeTab === 1) {
      setLoading(true);
      processPagination();
    } else {
      setFLoading(true);
      fetchFailedCustomers();
    }
  }

  return (
    <div className="container-fluid">
      <div className="row mt-5">
        <div className="col-3">
          <Label text={"Select Connector: "} />
        </div>
        <div className="col-9">
          <SelectInput
            options={connectors}
            selectedOptionValue={selectedConnector}
            onChange={(connector: string) => {
              Dispatch({
                Type: "SET_SELECTED_CONNECTOR",
                Payload: {
                  id: connector,
                },
              });
              setSelectedConnector(connector);
            }}
          />
        </div>
      </div>
      <br />
      {myobToNetoSync && !netoToMyobSync && <img src={myobToNeto} alt="myobToNeto" height="30rem" />}
      <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
        <TabPanel header={<b>{`Failed Customers (${failedTotal})`}</b>}>
          <CustomerGridErrorComponent
            customerData={customerFailedData}
            loading={fLoading}
            totalRecords={failedTotal}
            error={failedCustomersError}
            lastRefreshedAt={failedCustomersLastRefreshedAt}
            onRefresh={refreshFailedCustomers}
          />
        </TabPanel>
        <TabPanel header={<b>{`Synced Customers (${successTotal})`}</b>}>
          <CustomerGridComponent
            customerData={customersData}
            totalRecords={successTotal}
            isNextDisabled={isNextDisabled}
            isPrevDisabled={isPrevDisabled}
            lazyParams={lazyParams}
            loading={loading}
            setLoading={setLoading}
            selectedConnector={selectedConnector}
            setLazyParams={setLazyParams}
            setStartAfterRecord={setStartAfterRecord}
            setStartBeforeRecord={setStartBeforeRecord}
            setCustomerType={setCustomerType}
            customerType={customerType}
            error={customersError}
            lastRefreshedAt={customersLastRefreshedAt}
            onRefresh={refreshCustomers}
          />
        </TabPanel>
      </TabView>

    </div>
  );
};

export default CustomerGridContainer;
